import { inject, Injectable } from "@angular/core";
import { HttpClient } from "@angular/common/http";
import { Observable } from "rxjs";
import { map } from "rxjs/operators";
import { PaginatedEntityApiService } from "../../../../shared/store/pagination/paginated-entity-api.service";
import { QueryParam } from "../../../../shared/store/store-types.model";
import { queryParamToHttp } from "../../../../shared/store/utils/store-utils.service";
import { environment } from "../../../../../environments/environment";
import {
  CloseNonConformitePayload,
  CreateNonConformitePayload,
  NcAnalytics,
  NcStats,
  NcWarehouseBucket,
  NonConformite,
  QuickSearchResult,
  UpdateNonConformitePayload,
} from "../../models/non-conformite.model";
import {
  NC_CLOSURE_METRICS_DATA,
  NC_SUPPLIER_COMPLIANCE_DATA,
} from "../../../dashboard/models/data.model";

@Injectable({ providedIn: "root" })
export class NonConformiteApiService
  implements PaginatedEntityApiService<NonConformite>
{
  private httpClient = inject(HttpClient);

  loadOne(id: string): Observable<NonConformite> {
    return this.httpClient.get<NonConformite>(
      `${environment.api()}/non_conformites/id/${id}`,
    );
  }

  loadAll(_query?: QueryParam): Observable<NonConformite[]> {
    return this.httpClient
      .get<{ data: NonConformite[]; total: number }>(
        `${environment.api()}/non_conformites/search`,
        { params: queryParamToHttp({ pageSize: 200 }) },
      )
      .pipe(map((r) => r.data));
  }

  loadPage(
    page: number,
    limit: number,
    query?: QueryParam,
  ): Observable<{ data: NonConformite[]; total: number }> {
    const params = queryParamToHttp({ ...query, page, pageSize: limit });
    return this.httpClient.get<{ data: NonConformite[]; total: number }>(
      `${environment.api()}/non_conformites/search`,
      { params },
    );
  }

  create(payload: CreateNonConformitePayload): Observable<NonConformite> {
    return this.httpClient.post<NonConformite>(
      `${environment.api()}/non_conformites`,
      payload,
    );
  }

  update(entity: NonConformite): Observable<NonConformite> {
    const payload: UpdateNonConformitePayload = {
      gravite: entity.gravite,
      cause5M: entity.cause5M,
      description: entity.description,
      coutEstime: entity.coutEstime,
      photos: entity.photos,
      decision: entity.decision,
    };
    return this.httpClient.put<NonConformite>(
      `${environment.api()}/non_conformites/${entity.id}`,
      payload,
    );
  }

  patch(
    id: string,
    payload: UpdateNonConformitePayload,
  ): Observable<NonConformite> {
    return this.httpClient.put<NonConformite>(
      `${environment.api()}/non_conformites/${id}`,
      payload,
    );
  }

  remove(_id: string): Observable<string> {
    // Pas de hard-delete : on annule via /cancel
    throw new Error("NC : utiliser cancel() au lieu de remove()");
  }

  close(
    id: string,
    payload: CloseNonConformitePayload,
  ): Observable<NonConformite> {
    return this.httpClient.post<NonConformite>(
      `${environment.api()}/non_conformites/${id}/close`,
      payload,
    );
  }

  cancel(id: string, comment?: string): Observable<NonConformite> {
    return this.httpClient.post<NonConformite>(
      `${environment.api()}/non_conformites/${id}/cancel`,
      { comment },
    );
  }

  /**
   * Réouvre une NC clôturée — repasse en EN_TRAITEMENT pour permettre la
   * modification de la décision et des liens. Le back renvoie aussi
   * `hadClosureMovement: boolean` pour informer le front qu'un mouvement
   * de stock avait été émis à la clôture (que l'utilisateur devra peut-
   * être réaligner manuellement après modification de la décision).
   */
  reopen(
    id: string,
    comment?: string,
  ): Observable<{ nc: NonConformite; hadClosureMovement: boolean }> {
    return this.httpClient.post<{
      nc: NonConformite;
      hadClosureMovement: boolean;
    }>(`${environment.api()}/non_conformites/${id}/reopen`, { comment });
  }

  /** Rattache la `document_list` créée par `vl-document-list` à la NC. */
  updateDocumentListeId(
    id: string,
    listeId: string,
  ): Observable<NonConformite> {
    return this.httpClient.post<NonConformite>(
      `${environment.api()}/non_conformites/update_documentListId/id/${id}/listeId/${listeId}`,
      {},
    );
  }

  stats(start?: string, end?: string): Observable<NcStats> {
    const params = queryParamToHttp({ start, end });
    return this.httpClient.get<NcStats>(
      `${environment.api()}/non_conformites/stats`,
      { params },
    );
  }

  analytics(
    start?: string,
    end?: string,
    /** Filtres CSV optionnels (mêmes axes que la liste NC). */
    filters?: {
      etats?: string[];
      gravites?: string[];
      types?: string[];
      natureIds?: string[];
      origines?: string[];
      detections?: string[];
    },
  ): Observable<NcAnalytics> {
    const csv = (v?: string[]) => (v?.length ? v.join(",") : undefined);
    const params = queryParamToHttp({
      start,
      end,
      etats: csv(filters?.etats),
      gravites: csv(filters?.gravites),
      types: csv(filters?.types),
      natureIds: csv(filters?.natureIds),
      origines: csv(filters?.origines),
      detections: csv(filters?.detections),
    });
    return this.httpClient.get<NcAnalytics>(
      `${environment.api()}/non_conformites/analytics`,
      { params },
    );
  }

  byWarehouse(): Observable<NcWarehouseBucket[]> {
    return this.httpClient.get<NcWarehouseBucket[]>(
      `${environment.api()}/non_conformites/by_warehouse`,
    );
  }

  closureMetrics(start?: string, end?: string): Observable<NC_CLOSURE_METRICS_DATA> {
    const params = queryParamToHttp({ start, end });
    return this.httpClient.get<NC_CLOSURE_METRICS_DATA>(
      `${environment.api()}/non_conformites/closure_metrics`,
      { params },
    );
  }

  supplierCompliance(
    start?: string,
    end?: string,
    months?: number,
    limit?: number,
    detailed?: boolean,
  ): Observable<NC_SUPPLIER_COMPLIANCE_DATA> {
    const params = queryParamToHttp({
      start,
      end,
      months,
      limit,
      detailed: detailed ? "true" : undefined,
    });
    return this.httpClient.get<NC_SUPPLIER_COMPLIANCE_DATA>(
      `${environment.api()}/non_conformites/supplier_compliance`,
      { params },
    );
  }

  loadByLot(lotId: string): Observable<NonConformite[]> {
    return this.httpClient.get<NonConformite[]>(
      `${environment.api()}/non_conformites/lot/${lotId}`,
    );
  }

  /** NC liées directement à un OF (via `ofId`). Utilisé par le panneau
   *  "Impact NC" sur la page Stats OF. */
  loadByOf(ofId: string): Observable<NonConformite[]> {
    return this.httpClient.get<NonConformite[]>(
      `${environment.api()}/non_conformites/of/${ofId}`,
    );
  }

  loadByReception(receptionCommandeId: string): Observable<NonConformite[]> {
    return this.httpClient.get<NonConformite[]>(
      `${environment.api()}/non_conformites/reception/${receptionCommandeId}`,
    );
  }

  loadByCommandeFournisseur(commandeId: string): Observable<NonConformite[]> {
    return this.httpClient.get<NonConformite[]>(
      `${environment.api()}/non_conformites/commande_fournisseur/${commandeId}`,
    );
  }

  abandonProvision(id: string): Observable<NonConformite> {
    return this.httpClient.post<NonConformite>(
      `${environment.api()}/non_conformites/${id}/abandon_provision`,
      {},
    );
  }

  loadByPalette(paletteId: string): Observable<NonConformite[]> {
    return this.httpClient.get<NonConformite[]>(
      `${environment.api()}/non_conformites/palette/${paletteId}`,
    );
  }

  loadByChargement(chargementId: string): Observable<NonConformite[]> {
    return this.httpClient.get<NonConformite[]>(
      `${environment.api()}/non_conformites/chargement/${chargementId}`,
    );
  }

  loadByTiers(tiersId: string): Observable<NonConformite[]> {
    return this.httpClient.get<NonConformite[]>(
      `${environment.api()}/non_conformites/tiers/${tiersId}`,
    );
  }

  loadByArticle(articleId: string): Observable<NonConformite[]> {
    return this.httpClient.get<NonConformite[]>(
      `${environment.api()}/non_conformites/article/${articleId}`,
    );
  }

  quickSearch(q: string): Observable<QuickSearchResult[]> {
    const params = queryParamToHttp({ q });
    return this.httpClient.get<QuickSearchResult[]>(
      `${environment.api()}/non_conformites/quick_search`,
      { params },
    );
  }
}
